import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import StoreItem from "./StoreItem";
import CartDrawer from "./CartDrawer";
import { useCart } from "./CartContext";

function Home({ pageFilter = null, title = "Our Products" }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { addToCart, totalItems } = useCart();

  useEffect(() => {
    fetch(process.env.REACT_APP_GET_PRODUCTS_URL)
      .then(res => res.json())
      .then(data => {
        // Lambda now returns { statusCode, body } — parse body if needed
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setProducts(parsed.products || []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load products.');
        setLoading(false);
      });
  }, []);

  // Build a map of priceId -> unit_amount for quick lookup
  const resolvePrice = (defaultPrice) => {
    if (!defaultPrice) return 0;
    // With expand=['data.default_price'], default_price is the full Price object
    if (typeof defaultPrice === 'object' && defaultPrice.unit_amount !== undefined) {
      return defaultPrice.unit_amount;
    }
    return 0;
  };

  const resolvePriceId = (defaultPrice) => {
    if (!defaultPrice) return null;
    if (typeof defaultPrice === 'object') return defaultPrice.id;
    return defaultPrice;
  };

  const resolveCurrency = (defaultPrice) => {
    if (defaultPrice && typeof defaultPrice === 'object' && defaultPrice.currency) {
      return defaultPrice.currency;
    }
    return 'usd';
  };

  const filteredProducts = useMemo(() => {
    let result = products;
    if (pageFilter) {
      result = result.filter(
        (p) => p.metadata && p.metadata.page === pageFilter
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return result;
    return result.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [products, search, pageFilter]);

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap gap-2 px-4 py-2 border-bottom">
        <h2 className="mb-0 me-auto">{loading ? 'Loading...' : title}</h2>
        <Link to="/orders" className="btn btn-outline-secondary btn-sm">
          <i className="material-symbols-rounded me-1" style={{ fontSize: "1rem", verticalAlign: "middle" }}>receipt_long</i>
          Orders
        </Link>
        <button
          className="btn btn-white btn-sm"
          type="button"
          onClick={() => setCartOpen(true)}
        >
          <i className="material-symbols-rounded me-2">shopping_cart</i>
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </button>
      </div>

      {!loading && !error && (
        <div className="px-4 pt-3">
          <div className="input-group input-group-outline">
            <label className="form-label">Search products...</label>
            <input
              type="text"
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search products"
            />
            {search && (
              <button
                className="btn btn-link px-2 text-muted"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <i className="material-symbols-rounded">close</i>
              </button>
            )}
          </div>
          {search && (
            <p className="text-sm text-muted mt-1">
              {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''} for "{search}"
            </p>
          )}
        </div>
      )}

      {error && <p className="text-danger text-center mt-4">{error}</p>}

      {!loading && !error && (
        <div className="container" style={{ paddingTop: "200px" }}>
          {filteredProducts.length === 0 ? (
            <div className="text-center mt-5 text-muted">
              <i className="material-symbols-rounded" style={{ fontSize: "3rem" }}>search_off</i>
              <p className="mt-2">No products match "{search}"</p>
            </div>
          ) : (
            <div className="row">
              {filteredProducts.map(product => (
                <div className="col-12 mb-5" key={product.id}>
                  <StoreItem
                    id={product.id}
                    name={product.name}
                    description={product.description}
                    price={resolvePrice(product.default_price)}
                    priceId={resolvePriceId(product.default_price)}
                    currency={resolveCurrency(product.default_price)}
                    image={product.images && product.images[0]}
                    images={product.images || []}
                    metadata={product.metadata || {}}
                    onAddToCart={addToCart}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

export default Home;